/**
 * GuardRails service: creates course structures and child activities using course-type rules
 * from {@link ./GuardRailsCourseConfig}, and exposes allowed plugins and builder guard lists.
 * @module services/GuardRailsService
 */
import GuardrailsRules from "../GuardRailsService/GuardRailsCourseConfig";
import pluginService from "services/PluginService";
import activityService from "services/ActivityService";
import PathwayService from "services/PathwayService";
import InteractiveService from "services/InteractiveService";
import pluginTypes from "../../constants/pluginTypes";
import {ICON_LIBRARY_LIST} from "containers/IconLibraryManager/IconLibrarySelector/constants";
import featureFlagService, { featureKeys } from 'services/FeatureFlagService';
import { GUARDRAILS_COURSE_TYPE_OPTIONS } from "./GuardRailsCourseTypes";
import * as keyPaths from 'services/ActivityService/keyPaths';

export default (() => {
  let selectedCourse = GUARDRAILS_COURSE_TYPE_OPTIONS.BV;
  let authorUnitPluginList = [];
  let authorLessonPluginList = [];
  let authorScreenPluginList = [];
  let createdPathwayId = '';
  let lessonPathwayId = '';
  let unitLessonAndScreenDetails = {};

  /** @returns {Array<{name: string}>} Unit plugin entries allowed for the selected course type. */
  const getCourseUnitPlugins = () => {
    let unitPlugins = [];
    if (selectedCourse && GuardrailsRules[selectedCourse]) {
      unitPlugins = GuardrailsRules[selectedCourse].rules.unit;
    }
    return unitPlugins.plugins;
  };

  /** @returns {Array<{name: string}>} Lesson plugin entries allowed for the selected course type. */
  const getCourseLessonPlugins = () => {
    let lessonPlugins = [];
    if (selectedCourse && GuardrailsRules[selectedCourse]) {
      lessonPlugins = GuardrailsRules[selectedCourse].rules.lesson;
    }
    return lessonPlugins.plugins;
  };

  /**
   * @param {string} courseType - GuardRails course key (e.g. BV, EPUB).
   * @returns {Array<{name: string, description?: string}>} Screen plugin entries for the course.
   */
  const getCourseScreenPlugins = (courseType) => {
    let screenPlugins = [];
    if (courseType && GuardrailsRules[courseType]) {
      screenPlugins = GuardrailsRules[courseType].rules.screen;
    }
    return screenPlugins.plugins;
  };

  /**
   * Creates a unit, lesson, or screen subtree from user-provided titles and GuardRails config.
   * @param {string} parentPathwayId - Pathway under which to create the activity.
   * @param {{unitName?: string, lessonName?: string, screenName?: string, screenType?: string}} UnitLessonDetails
   * @param {string} serviceType - One of pluginTypes.UNIT | LESSON | SCREEN.
   * @param {string} courseType - GuardRails course key.
   */
  const createUnitAndLessons = async (parentPathwayId, UnitLessonDetails, serviceType, courseType) => {
    unitLessonAndScreenDetails = UnitLessonDetails;
    
    selectedCourse = courseType;
    try {
      if (serviceType === pluginTypes.UNIT) {
        await createUnitActivity(parentPathwayId);
      } else if (serviceType === pluginTypes.LESSON) {
        await createLessonActivity(parentPathwayId);
      } else if (serviceType === pluginTypes.SCREEN) {
        await createScreenActivity(parentPathwayId);
      }
    } catch (error) {
      throw new Error(`error occurred while creating the unit and lessons: , ${error}.`);
    }

  };

  /**
   * Creates a unit activity and nested lesson/screen per GuardRails rules for the active course type.
   * @param {string} parentPathwayId
   * @returns {Promise<string|null>} New unit pathway id, or null if parent pathway missing.
   */
  const createUnitActivity = async (parentPathwayId) => {
    const { unit } = GuardrailsRules[selectedCourse].rules;
    const  pathwayId =  parentPathwayId;

    let title = unitLessonAndScreenDetails?.unitName;
    title = title || "Untitled Unit";
    let description = "Untitled Unit Description";

    authorUnitPluginList = await pluginService.listPluginsByType('unit');

    if (authorUnitPluginList.length === 0) {
      throw new Error('No unit plugins are available for this workspace. Install a unit plugin that matches Guardrails configuration.');
    }

    const expectedUnitName = unit.plugins[0].name;
    const unitPluginMatch = authorUnitPluginList.find((item) => item.name === expectedUnitName);
    if (!unitPluginMatch) {
      throw new Error(
        `No unit plugin named "${expectedUnitName}" was found. Check Guardrails config matches installed unit plugins.`,
      );
    }
    const { pluginId, latestVersion } = unitPluginMatch;

    const pluginDetails = await pluginService.getPlugin(pluginId, latestVersion);
    const { manifest: childManifest } = pluginDetails;

    if (pathwayId) {
      const parsedConfigurationSchema = JSON.parse(childManifest.configurationSchema);
      const activityId = await activityService.createActivity(
        pluginId,
        latestVersion,
        parsedConfigurationSchema,
        { title, description },
        false,
        pathwayId,
      );

      createdPathwayId = await PathwayService.createPathway(activityId, ['lessons'], 'FREE', parsedConfigurationSchema['lessons']?.preload);
      await createLessonActivity();
      return createdPathwayId;
    }

    return null;
  };

  /**
   * Creates a lesson under the current unit pathway, then creates its first screen.
   * @param {{pathwayId: string}|null} [lessonParentPathwayId] - When set, uses this pathway as parent.
   */
  const createLessonActivity = async (lessonParentPathwayId = null) => {
    const { lesson } = GuardrailsRules[selectedCourse].rules;
    if (lessonParentPathwayId) {
      createdPathwayId = lessonParentPathwayId.pathwayId;
    }
    let title = unitLessonAndScreenDetails?.lessonName;
    if (authorLessonPluginList.length === 0) {
      authorLessonPluginList = await pluginService.listPluginsByType('lesson');
    }
  
    title = title || "Untitled Lesson";
    let description = "Untitled Lesson Description";
    if (authorLessonPluginList) {
      const expectedLessonName = lesson.plugins[0].name;
      const lessonPluginMatch = authorLessonPluginList.find((item) => item.name === expectedLessonName);
      if (!lessonPluginMatch) {
        throw new Error(
          `No lesson plugin named "${expectedLessonName}" was found. Check Guardrails config matches installed lesson plugins.`,
        );
      }
      const { pluginId, latestVersion } = lessonPluginMatch;
      const lessonPluginDetails = await pluginService.getPlugin(pluginId, latestVersion);
      const { manifest: childManifest } = lessonPluginDetails;
      if (createdPathwayId) {
        const parsedConfigurationSchema = JSON.parse(childManifest.configurationSchema);
        const activityId = await activityService.createActivity(
          pluginId,
          latestVersion,
          parsedConfigurationSchema,
          { title, description }, 
          false,
          createdPathwayId,
        );

        lessonPathwayId = await PathwayService.createPathway(activityId, ['pathway'], 'FREE', parsedConfigurationSchema['pathway']?.preload);
        await createScreenActivity();
      }
    }
  };

  /**
   * Creates a screen interactive on the lesson pathway using configured or user-selected screen plugin.
   * @param {string|null} [parentPathwayId] - When set, overrides lesson pathway for placement.
   * @returns {Promise<string|undefined>} Interactive id when created.
   */
  const createScreenActivity = async (parentPathwayId = null) => {
    const { screen } = GuardrailsRules[selectedCourse].rules;
    let title = unitLessonAndScreenDetails?.screenName;
    title = title || "Untitled Screen";
    let description = "Untitled Screen Description";
    if (authorScreenPluginList.length === 0) {
      authorScreenPluginList = await pluginService.listPluginsByType('screen');
    }

    if (parentPathwayId) {
      lessonPathwayId = parentPathwayId;
    }
    if (authorScreenPluginList) {
      const screenPluginName = unitLessonAndScreenDetails?.screenType || screen.plugins[0].name;
      const screenPluginMatch = authorScreenPluginList.find((item) => item.name === screenPluginName);
      if (!screenPluginMatch) {
        throw new Error(
          `No screen plugin named "${screenPluginName}" was found. Check Guardrails config matches installed screen plugins.`,
        );
      }
      const { pluginId, latestVersion } = screenPluginMatch;

      if (lessonPathwayId) {
        const { manifest: { configurationSchema } } = await pluginService.getPlugin(pluginId, latestVersion);
        const screenSchema = JSON.parse(configurationSchema);
        const interactiveId = await InteractiveService.createInteractive(
          lessonPathwayId,
          pluginId,
          latestVersion,
          title,
          screenSchema,
          description,
        );
        return interactiveId;
      }
    }
  };

  /**
   * @param {string} selectedCourseType
   * @returns {string[]} Tool names allowed in the screen builder for the course.
   */
  const getToolPlugins = (selectedCourseType) => {
    if (!isGuardRailsEnabled()) return [];
    if (!selectedCourseType || !GuardrailsRules[selectedCourseType]) return [];
    const { tools } = GuardrailsRules[selectedCourseType].rules.buildComponentGuardRules;
    return tools;
  };

  /**
   * @param {string} selectedCourseType
   * @returns {string[]} Component plugin display names allowed in the screen builder.
   */
  const getComponentPluginsList = (selectedCourseType) => {
    if (!isGuardRailsEnabled()) return [];
    if (!selectedCourseType || !GuardrailsRules[selectedCourseType]) return [];
    const { components } = GuardrailsRules[selectedCourseType].rules.buildComponentGuardRules;
    return components;
  };

  /**
   * Looks up an installed plugin by type and name.
   * @param {'course'|'screen'|'unit'|'lesson'|'chapter'} pluginType
   * @param {string} pluginName
   * @returns {Promise<object|undefined>} Plugin row from PluginService, if found.
   */
  const getFilteredPluginWithDetails = async (pluginType, pluginName) => {
    const validPluginTypes = ['course','screen','unit','lesson','chapter'];
    if(!validPluginTypes.includes(pluginType)) return;

    if(pluginType && pluginName){
      const pluginListByType = await pluginService.listPluginsByType(pluginType);
      const pluginDetails = pluginListByType?.find((p) => p.name === pluginName);
      return pluginDetails;
    }
  };

  /**
   * Creates a project course activity, default pathways from config, and icon library association.
   * @param {string} projectId
   * @param {string} coursePluginName - Installed course plugin name matching Guardrails rules.
   * @param {string} courseType - GuardRails course key.
   * @param {string} title
   * @param {boolean} isSlateJSGuardrailEnabled
   * @param {string} [description]
   * @returns {Promise<{pathwayId: string|null, activityId: string}|undefined>}
   */
  const createCourseWithGuardRails = async(projectId, coursePluginName, courseType, title, isSlateJSGuardrailEnabled, description = 'course description', ) => {
    const coursePathWays = GuardrailsRules[courseType].rules.coursePathWays.pathWays;
    if(projectId && coursePluginName && courseType && title) {
      selectedCourse = courseType;
      const coursePluginDetails = await getFilteredPluginWithDetails('course',coursePluginName);
      if(coursePluginDetails) {
        const { pluginId, latestVersion } = coursePluginDetails;
        const { manifest: { configurationSchema } } = await pluginService.getPlugin(pluginId, latestVersion);
        const schema = JSON.parse(configurationSchema);
        const newActivityConfig = {title, description, [keyPaths.guardRailsOptions]: courseType, supportedScreenVersion: isSlateJSGuardrailEnabled ? '2.*' : '0.*'};
        const activityId = await activityService.createProjectActivity(
          pluginId, latestVersion, schema, newActivityConfig, projectId
        );
 
        //Associate Default IconLibraryList with the given activity
        await activityService.authorActivityIconLibraryAssociate({
          activityId,
          iconLibraries: ICON_LIBRARY_LIST,
        });
       
        if (coursePathWays) {
          const coursePathwayIds = await Promise.all(coursePathWays.map((pathwayType) => PathwayService.createPathway(activityId, pathwayType.key, pathwayType.value, schema[pathwayType.key[0]]?.preload)));
          return { pathwayId: coursePathwayIds[2], activityId };
        }
 
        return { pathwayId: null, activityId };
 
      }
    }
  };

  const isGuardRailsEnabled = () => featureFlagService.is(featureKeys.BPRT_455_AUTHORING_GUARDRAILS);

  const isGuardRailsOptionsSettingEnabled = () =>
    isGuardRailsEnabled() && featureFlagService.is(featureKeys.BRNTB_13_GUARDRAILS_OPTIONS_SETTING);

  return {
    getCourseUnitPlugins,
    getCourseLessonPlugins,
    getCourseScreenPlugins,
    createUnitAndLessons,
    createUnitActivity,
    getToolPlugins,
    getComponentPluginsList,
    getFilteredPluginWithDetails,
    createCourseWithGuardRails,
    isGuardRailsEnabled,
    isGuardRailsOptionsSettingEnabled,
  };
})();
